import { FEATURE_FLAG, FeatureFlag, FeatureFlags } from "./feature-flags/feature-flags";
import { PAGE_PERMISSION, PagePermission, PagePermissions } from "./page-permissions/page-permissions";

export interface AccessControlRequirements {
    readonly requiredPagePermissions?: readonly PagePermission[];
    readonly requiredFeatureFlags?: readonly FeatureFlag[];
}

export interface AccessControlContext {
    readonly pagePermissions: PagePermissions | null | undefined;
    readonly featureFlags: Partial<FeatureFlags>;
}

/**
 * Defines the permissions and feature flags needed for each app area.
 *
 * Page permissions are derived from the signed-in user's roles and access.
 * Feature flags are enabled by the deployment configuration.
 * An area is available only when all of its listed requirements are met.
 *
 * Routes use these requirements to allow or deny access.
 * Navigation items use them to hide areas the user cannot access.
 */
export const ACCESS_CONTROL = {
    ACCOUNTS: {
        requiredPagePermissions: [PAGE_PERMISSION.ACCOUNTS],
    },
    ADMIN: {
        requiredPagePermissions: [PAGE_PERMISSION.ADMIN],
    },
    BROWSE: {
        requiredPagePermissions: [PAGE_PERMISSION.BROWSE],
    },
    CERTIFICATE_AUTHORITIES: {
        requiredPagePermissions: [PAGE_PERMISSION.CERTIFICATE_AUTHORITIES],
    },
    CERTIFICATES: {
        requiredPagePermissions: [PAGE_PERMISSION.CERTIFICATES],
    },
    CERTIFICATE_MANAGEMENT: {
        requiredPagePermissions: [PAGE_PERMISSION.CERTIFICATES, PAGE_PERMISSION.MANAGE_CERTIFICATES],
    },
    DASHBOARD: {
        requiredPagePermissions: [PAGE_PERMISSION.DASHBOARD],
    },
    DOMAINS: {
        requiredPagePermissions: [PAGE_PERMISSION.DOMAINS],
    },
    HELP: {
        requiredPagePermissions: [PAGE_PERMISSION.HELP],
    },
    LOGS: {
        requiredPagePermissions: [PAGE_PERMISSION.LOGS],
    },
    PROFILE: {
        requiredPagePermissions: [PAGE_PERMISSION.PROFILE],
    },
    REPORTS: {
        requiredPagePermissions: [PAGE_PERMISSION.REPORTS],
    },
    REQUESTS: {
        requiredPagePermissions: [PAGE_PERMISSION.REQUESTS],
    },
    TLS_DISCOVERY: {
        requiredPagePermissions: [PAGE_PERMISSION.TLS_DISCOVERY],
        requiredFeatureFlags: [FEATURE_FLAG.TLS_DISCOVERY],
    },
    USERS: {
        requiredPagePermissions: [PAGE_PERMISSION.USERS],
    },
} as const satisfies Record<string, AccessControlRequirements>;

export function hasPageAccess({
    requirements,
    context,
}: {
    requirements: AccessControlRequirements;
    context: AccessControlContext;
}): boolean {
    return (
        (requirements.requiredPagePermissions ?? []).every((permission) => !!context.pagePermissions?.[permission]) &&
        (requirements.requiredFeatureFlags ?? []).every((featureFlag) => context.featureFlags[featureFlag])
    );
}
